import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { ModalShell, ModalHeader } from '../../../components/ui/ModalFrame';
import PaymentMethodFormContent from './PaymentMethodFormContent';
import licenseService from '../../../services/licenseService';
import userService from '../../../services/userService';
import { buildPaymentPayload } from 'utils/buildPaymentPayload';
import { mapPaymentError } from 'utils/mapPaymentError';
import { openMercadoPagoCheckout } from 'utils/openMercadoPagoCheckout';
import { useMercadoPagoSdk } from '../../../hooks/useMercadoPagoSdk';
import { MODAL_LAYER } from '../../../constants/modalLayers';
import { MODAL_FOOTER_PAYMENT_ACTIONS } from '../../../constants/layoutSpacing';
import { formatLicensePriceOrConsult } from '../../../utils/formatCurrencyBRL';
import { isValidCpf } from 'utils/masks';
import PixCheckoutPanel from './PixCheckoutPanel';
import CardCheckoutPanel from './CardCheckoutPanel';
import CardPaymentResultPanel from './CardPaymentResultPanel';

const PaymentMethodModal = ({ license, onClose }) => {
  const {
    sdk,
    ready,
    loading: sdkLoading,
    error: sdkError,
    sandboxCheckout,
    embeddedCardCheckout,
    testPayerEmail,
    checkoutHints,
  } = useMercadoPagoSdk();

  const [paymentType, setPaymentType] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerCpf, setCustomerCpf] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [pixCheckout, setPixCheckout] = useState(null);
  const [cardPaymentResult, setCardPaymentResult] = useState(null);

  useEffect(() => {
    setErrorMessage(null);
  }, [paymentType, customerName, customerCpf]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAccountEmail = async () => {
      const result = await userService.getUserInfo();
      if (!cancelled && result?.success && result.data?.email) {
        setAccountEmail(result.data.email);
      }
    };

    loadAccountEmail();
    return () => {
      cancelled = true;
    };
  }, []);

  const isFormValid = () => {
    if (!paymentType) return false;
    return customerName.trim().length > 0 && isValidCpf(customerCpf);
  };

  const submitDisabledReason = () => {
    if (!paymentType) return 'Selecione uma forma de pagamento';
    if (!customerName.trim()) return 'Informe seu nome completo';
    if (!isValidCpf(customerCpf)) return 'Informe um CPF válido';
    return null;
  };

  const useEmbeddedCardCheckout =
    paymentType === 'CREDIT_CARD' && ready && (embeddedCardCheckout || sandboxCheckout);

  const handleSubmit = async () => {
    if (useEmbeddedCardCheckout) return;

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const payload = buildPaymentPayload({
        paymentType,
        license,
        customerName,
        customerCpf,
      });

      const response = await licenseService.generatePaymentOrder(payload);

      if (!response?.success) {
        throw new Error(response?.error || 'Erro ao processar pagamento');
      }

      if (response.PIX?.qr_code || response.PIX?.qr_code_base64 || response.PIX?.ticket_url) {
        setPixCheckout(response.PIX);
        setIsSubmitting(false);
        return;
      }

      const checkout = response?.CHECKOUT_PRO ?? {};
      const checkoutUrl = checkout.checkout_url || checkout.sandbox_init_point || checkout.init_point;
      const preferenceId = response.preference_id || response.order_id;

      if (!checkoutUrl && !preferenceId) {
        throw new Error('Checkout do Mercado Pago não retornou URL de pagamento');
      }

      await openMercadoPagoCheckout({
        sdk: ready ? sdk : null,
        preferenceId,
        checkoutUrl,
      });
    } catch (err) {
      setErrorMessage(err.message || mapPaymentError(err));
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell
      backdropClassName="bg-background/80"
      zClass={MODAL_LAYER.payment}
      panelClassName="w-full max-w-2xl h-[95vh] bg-card border rounded-lg flex flex-col shadow-elevation-lg"
      backdropPadding="none"
    >
      <ModalHeader
        title="Pagamento via Mercado Pago"
        onClose={onClose}
        titleClassName="text-xl font-semibold"
      />

      <div className="px-6 pt-2">
        <div className="rounded-lg border border-border bg-muted/20 p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Licença selecionada</p>
            <p className="font-semibold text-foreground">{license?.license_name}</p>
          </div>
          <p className="text-lg font-heading font-bold text-foreground">
            {formatLicensePriceOrConsult(license?.price)}
          </p>
        </div>
      </div>

      <PaymentMethodFormContent
        paymentType={paymentType}
        onPaymentTypeChange={setPaymentType}
        customerName={customerName}
        onCustomerNameChange={setCustomerName}
        customerCpf={customerCpf}
        onCustomerCpfChange={setCustomerCpf}
        accountEmail={accountEmail}
        errorMessage={errorMessage}
        license={license}
        sdkLoading={sdkLoading}
        sdkError={sdkError}
        sandboxCheckout={sandboxCheckout}
        testPayerEmail={testPayerEmail}
        checkoutHints={checkoutHints}
      />

      {cardPaymentResult ? (
        <div className="px-6 pb-4">
          <CardPaymentResultPanel result={cardPaymentResult} onClose={onClose} />
        </div>
      ) : pixCheckout ? (
        <div className="px-6 pb-4">
          <PixCheckoutPanel
            pixData={pixCheckout}
            license={license}
            onClose={onClose}
          />
        </div>
      ) : useEmbeddedCardCheckout && isFormValid() && accountEmail ? (
        <div className="px-6 pb-4 space-y-4">
          <CardCheckoutPanel
            sdk={sdk}
            amount={license?.price}
            license={license}
            accountEmail={accountEmail}
            customerName={customerName}
            customerCpf={customerCpf}
            onPaymentResult={setCardPaymentResult}
            onError={setErrorMessage}
          />
          <Button variant="outline" fullWidth onClick={onClose}>
            Voltar
          </Button>
        </div>
      ) : (
        <div className={MODAL_FOOTER_PAYMENT_ACTIONS}>
          <Button variant="outline" fullWidth onClick={onClose}>
            Voltar
          </Button>
          <div className="flex-1 space-y-2">
            {!isFormValid() && paymentType && (
              <p className="text-xs text-muted-foreground text-center">
                {submitDisabledReason()}
              </p>
            )}
            <Button
              fullWidth
              disabled={!isFormValid() || isSubmitting || useEmbeddedCardCheckout}
              loading={isSubmitting}
              onClick={handleSubmit}
              iconName="ExternalLink"
              iconPosition="right"
            >
              {paymentType === 'PIX'
                ? 'Gerar PIX'
                : paymentType === 'CREDIT_CARD' && !ready
                  ? 'Ir para o Mercado Pago'
                  : paymentType === 'CREDIT_CARD'
                    ? 'Carregando cartão...'
                    : 'Ir para o Mercado Pago'}
            </Button>
          </div>
        </div>
      )}
    </ModalShell>
  );
};

export default PaymentMethodModal;
